import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, FileCheck, Scale } from 'lucide-react';
import { get, post, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { tenantUsesArabic } from '@/lib/locale-fit';
import { humanize } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Checkbox, Field, FormSkeleton, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { insuranceTabPath } from './insurance-routes';

export interface Payer {
  id: string;
  code: string;
  name: string;
  kind: string;
  coPayPct: number;
  requiresPreAuth: boolean;
  claimTatDays: number;
  isActive: boolean;
  tpaPayerId?: string | null;
  submissionWindowDays?: number | null;
  resubmissionWindowDays?: number | null;
  claimGrain?: string | null;
  nphiesLicense?: string | null;
  nphiesOrgType?: string | null;
  vatNumber?: string | null;
  legalName?: string | null;
  legalNameLocal?: string | null;
}

interface PayerForm {
  code: string;
  name: string;
  kind: string;
  coPayPct: number;
  requiresPreAuth: boolean;
  claimTatDays: number;
  tpaPayerId: string;
  submissionWindowDays: string;
  resubmissionWindowDays: string;
  claimGrain: string;
  nphiesLicense: string;
  nphiesOrgType: string;
  vatNumber: string;
  legalName: string;
  legalNameLocal: string;
}

const EMPTY_PAYER: PayerForm = { code: '', name: '', kind: 'insurance', coPayPct: 0, requiresPreAuth: false, claimTatDays: 30, tpaPayerId: '', submissionWindowDays: '', resubmissionWindowDays: '', claimGrain: '', nphiesLicense: '', nphiesOrgType: '', vatNumber: '', legalName: '', legalNameLocal: '' };

function formOf(payer: Payer | null): PayerForm {
  if (!payer) return EMPTY_PAYER;
  return {
    code: payer.code,
    name: payer.name,
    kind: payer.kind,
    coPayPct: payer.coPayPct,
    requiresPreAuth: payer.requiresPreAuth,
    claimTatDays: payer.claimTatDays,
    tpaPayerId: payer.tpaPayerId ?? '',
    submissionWindowDays: payer.submissionWindowDays ? String(payer.submissionWindowDays) : '',
    resubmissionWindowDays: payer.resubmissionWindowDays ? String(payer.resubmissionWindowDays) : '',
    claimGrain: payer.claimGrain ?? '',
    nphiesLicense: payer.nphiesLicense ?? '',
    nphiesOrgType: payer.nphiesOrgType ?? '',
    vatNumber: payer.vatNumber ?? '',
    legalName: payer.legalName ?? '',
    legalNameLocal: payer.legalNameLocal ?? '',
  };
}

function payerBody(form: PayerForm, nphies: boolean) {
  const days = (value: string) => (value.trim() === '' ? null : Number(value));
  return {
    code: form.code,
    name: form.name,
    kind: form.kind,
    coPayPct: form.coPayPct,
    requiresPreAuth: form.requiresPreAuth,
    claimTatDays: form.claimTatDays,
    tpaPayerId: form.tpaPayerId || null,
    submissionWindowDays: days(form.submissionWindowDays),
    resubmissionWindowDays: days(form.resubmissionWindowDays),
    claimGrain: form.claimGrain || null,
    vatNumber: form.vatNumber.trim() || null,
    legalName: form.legalName.trim() || null,
    legalNameLocal: form.legalNameLocal.trim() || null,
    ...(nphies ? { nphiesLicense: form.nphiesLicense.trim() || null, nphiesOrgType: (form.nphiesOrgType || null) as 'ins' | 'tpa' | null } : {}),
  };
}

export function PayerFormPage() {
  const { payerId } = useParams<{ payerId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), insuranceTabPath('payers'));
  const payers = useQuery({
    queryKey: ['payers', 'all'],
    queryFn: () => get<Payer[]>('/insurance/payers?includeInactive=true'),
  });
  const editing = payerId ? (payers.data?.find((row) => row.id === payerId) ?? null) : null;

  if (payerId && payers.isLoading) return <FormSkeleton />;
  if (payerId && !editing) return <Alert variant="destructive">{payers.error ? describeError(payers.error) : 'This payer is not on file.'}</Alert>;

  return <PayerEditor key={editing?.id ?? 'new'} editing={editing} payers={payers.data ?? []} returnTo={returnTo} />;
}

function PayerEditor({ editing, payers, returnTo }: { editing: Payer | null; payers: Payer[]; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const nphies = auth.hasModule('nphies');
  const arabic = tenantUsesArabic(auth.user);
  const initial = formOf(editing);
  const [form, setForm] = useState<PayerForm>(initial);

  const save = useMutation({
    mutationFn: () => (editing ? put(`/insurance/payers/${editing.id}`, payerBody(form, nphies)) : post('/insurance/payers', payerBody(form, nphies))),
    onSuccess: () => {
      toast.success('Payer saved');
      void queryClient.invalidateQueries({ queryKey: ['payers'] });
      navigate(returnTo, { replace: true });
    },
    onError: (error) => toast.error('Could not save the payer', describeError(error)),
  });

  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={editing ? editing.name : 'Add a payer'}
      description="Co-pay and pre-auth here become the defaults at the desk"
      icon={Building2}
      eyebrow={tr('billing', 'payer')}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (form.code && form.name) save.mutate();
      }}
    >
      <FormSection title={tr('billing', 'identity')} icon={Building2}>
        <Field label="Code" required>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
        </Field>
        <Field label="Name" required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Kind">
          <ChoiceButtons
            layout="chips"
            value={form.kind}
            onChange={(kind) => setForm({ ...form, kind })}
            options={['insurance', 'corporate', 'government', 'ngo'].map((k) => ({ value: k, label: humanize(k, 'payer_kind') }))}
          />
        </Field>
        <Field label="Administered by" hint="The TPA that handles this payer’s claims, if any">
          <Select value={form.tpaPayerId} onChange={(e) => setForm({ ...form, tpaPayerId: e.target.value })}>
            <option value="">Nobody — the payer itself</option>
            {payers.filter((row) => row.id !== editing?.id).map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'claim_rules')} icon={FileCheck}>
        <Field label="Co-pay %" hint="What the patient pays at the desk">
          <Input type="number" min={0} max={100} value={form.coPayPct} onChange={(e) => setForm({ ...form, coPayPct: Number(e.target.value) })} />
        </Field>
        <Field label="Claim turnaround (days)">
          <Input type="number" min={1} value={form.claimTatDays} onChange={(e) => setForm({ ...form, claimTatDays: Number(e.target.value) })} />
        </Field>
        <Checkbox className="self-end" checked={form.requiresPreAuth} onCheckedChange={(checked) => setForm({ ...form, requiresPreAuth: checked })} label="Requires pre-authorisation" />
        <Field label="Claims are made">
          <ChoiceButtons
            value={form.claimGrain}
            onChange={(claimGrain) => setForm({ ...form, claimGrain })}
            options={[
              { value: '', label: 'One per patient' },
              { value: 'per_payer_period', label: 'One per payer per period' },
            ]}
          />
        </Field>
        <Field label="Days allowed to submit a claim" hint="From the first service">
          <Input type="number" min={1} value={form.submissionWindowDays} onChange={(e) => setForm({ ...form, submissionWindowDays: e.target.value })} />
        </Field>
        <Field label="Days allowed to resubmit" hint="From the payer’s answer">
          <Input type="number" min={1} value={form.resubmissionWindowDays} onChange={(e) => setForm({ ...form, resubmissionWindowDays: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'legal_details')} icon={Scale}>
        <Field label="Legal name">
          <Input value={form.legalName} onChange={(e) => setForm({ ...form, legalName: e.target.value })} />
        </Field>
        <Field label="VAT / tax number">
          <Input value={form.vatNumber} onChange={(e) => setForm({ ...form, vatNumber: e.target.value })} />
        </Field>
        {nphies && (
          <>
            {arabic && (
              <Field label="Legal name (Arabic)">
                <Input value={form.legalNameLocal} onChange={(e) => setForm({ ...form, legalNameLocal: e.target.value })} />
              </Field>
            )}
            <Field label="NPHIES licence">
              <Input value={form.nphiesLicense} onChange={(e) => setForm({ ...form, nphiesLicense: e.target.value })} />
            </Field>
            <Field label="NPHIES organisation type">
              <ChoiceButtons
                value={form.nphiesOrgType}
                onChange={(nphiesOrgType) => setForm({ ...form, nphiesOrgType })}
                onClear={() => setForm({ ...form, nphiesOrgType: '' })}
                options={[
                  { value: 'ins', label: 'Insurer' },
                  { value: 'tpa', label: 'TPA' },
                ]}
              />
            </Field>
          </>
        )}
      </FormSection>

      <FormActions submitLabel={tr('billing', 'save')} loading={save.isPending} incomplete={!form.code || !form.name} cancelTo={returnTo} />
    </FormScreen>
  );
}
